import { Node } from "@tiptap/core";

import { EditorMode, type CustomCheckboxOptions } from "../interfaces";

import { hasValue } from "@/Utils/Objects";

import { getRandomId } from "./utils";

interface insertCustomCheckboxOptions {
  checked?: boolean;
  name: string;
}
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    customCheckbox: {
      insertCustomCheckbox: (options?: insertCustomCheckboxOptions) => ReturnType;
    };
  }
}

const CustomCheckbox = Node.create<CustomCheckboxOptions>({
  name: "customCheckbox",

  group: "block",
  content: "inline*", // para que el label sea editable
  atom: false,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      mode: EditorMode.EDITOR,
      onChange: undefined,
    };
  },

  addAttributes() {
    return {
      checked: {
        default: false,
        parseHTML: (element) => element.querySelector("input")?.checked || false,
        renderHTML: (attributes) => ({
          checked: attributes.checked,
        }),
      },
      name: {
        default: `checkbox_${getRandomId()}`,
        parseHTML: (element) => element.getAttribute("name") || `checkbox_${getRandomId()}`,
        renderHTML: (attributes) => ({ name: attributes.name }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: ".html_doc__checkbox",
        getAttrs: (el) => {
          return { name: el.getAttribute("name") || `checkbox_${getRandomId()}` };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const { name } = HTMLAttributes;

    return ["span", { class: "html_doc__checkbox", name }, 0]; // el contenido (label) va en el slot 0
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const wrapper = document.createElement("span");
      const checkbox = document.createElement("input");
      const contentDOM = document.createElement("span");
      const label = document.createElement("span");
      const checkmark = document.createElement("span");

      checkbox.type = "checkbox";
      checkbox.checked = node.attrs.checked;
      checkbox.disabled = true;
      checkbox.tabIndex = -1;
      checkbox.name = node.attrs.name;

      contentDOM.style.display = "inline";

      checkmark.className = "checkmark";

      label.className = "editable-label";
      label.appendChild(contentDOM);

      wrapper.className = "html_doc__checkbox";
      wrapper.appendChild(checkbox);
      wrapper.appendChild(label);
      wrapper.appendChild(checkmark);

      const updateValue = () => {
        const pos = getPos();

        if (hasValue(pos)) {
          if (hasValue(this.options.onChange)) {
            this.options.onChange({ name: node.attrs.name, value: !node.attrs.checked });
          }

          const tr = editor.state.tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            checked: !node.attrs.checked,
          });

          editor.view.dispatch(tr);
        }
      };

      if (this.options.mode === EditorMode.COMPLETE) {
        wrapper.className = `${wrapper.className} html_doc__checkbox--mode_complete`;
        checkmark.addEventListener("click", updateValue);
        label.addEventListener("click", updateValue);
      }

      return {
        dom: wrapper,
        contentDOM,
        destroy: () => {
          checkmark.removeEventListener("click", updateValue);
          label.removeEventListener("click", updateValue);
        },
      };
    };
  },

  addCommands() {
    return {
      insertCustomCheckbox:
        (options: Partial<insertCustomCheckboxOptions> = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: "customCheckbox",
            attrs: {
              checked: options.checked ?? false,
              name: options.name ?? `checkbox_${getRandomId()}`,
            },
            content: [
              {
                type: "text",
                text: "Etiqueta editable",
              },
            ],
          });
        },
    };
  },
});

export default CustomCheckbox;
